import isString from "lodash/isString";
import * as Tooltip from "@radix-ui/react-tooltip";
import React, { forwardRef } from "react";
import { Simulate } from "react-dom/test-utils";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import Icon from "@/shared/ui/Icon";

import load = Simulate.load;

type Props = {
  type?: string;
  placeholder?: string;
  title?: string;
  hiddenTitle?: boolean;
  icon?: React.ReactNode;
  isIconRight?: boolean;
  valid?: string;
  invalid?: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  className?: string;
  inputClassName?: string;
  required?: boolean;
  children?: React.ReactNode;
  info?: React.ReactNode | string;
  loading?: boolean;
  disabled?: boolean;
  passwordVisible?: boolean;
  onChangePasswordVisible?: (flag: boolean) => void;
  tabIndex?: number;
  maxLength?: number;
};

const TextInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      type = "text",
      placeholder,
      title,
      hiddenTitle,
      icon,
      isIconRight,
      valid,
      invalid,
      value = "",
      onChange,
      onBlur,
      className,
      inputClassName = "",
      required = false,
      info,
      children,
      loading,
      disabled,
      passwordVisible,
      onChangePasswordVisible,
      tabIndex,
      maxLength,
    },
    ref,
  ) => {
    const inputId = `text-input-${Math.random().toString(36).slice(2, 11)}`;
    const isPassword = type === "password";

    return (
      <div className={cn("control-wrapper", { "control-wrapper--has-value": value }, className)}>
        {title && (
          <div className={clsx("control-label flex items-center gap-2", { "mb-0": hiddenTitle })}>
            <label htmlFor={inputId} className={clsx({ "sr-only": hiddenTitle })}>
              {title}
              {required && <span className="text-danger-text ml-1">*</span>}
            </label>
            {info && (
              <Tooltip.Provider delayDuration={250}>
                <Tooltip.Root>
                  <Tooltip.Trigger type="button" tabIndex={-1}>
                    <Icon name="info" className="w-4" />
                  </Tooltip.Trigger>
                  <Tooltip.Portal container={document.getElementById("theme-root")}>
                    <Tooltip.Content
                      side="right"
                      align="start"
                      sideOffset={10}
                      alignOffset={-10}
                      className="control-info-tooltip"
                    >
                      {info}
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            )}
          </div>
        )}
        <div className="control-inner">
          {icon && (
            <div className={clsx(isIconRight ? "control-right right-4" : "control-left")}>
              {icon}
            </div>
          )}
          <input
            id={inputId}
            type={passwordVisible ? "text" : type}
            placeholder={placeholder}
            value={value ?? ""}
            onChange={(e) => {
              e.stopPropagation();
              onChange(e.target.value);
            }}
            onBlur={onBlur}
            required={required}
            className={cn("control", { "control--error": invalid }, inputClassName)}
            disabled={disabled}
            ref={ref}
            tabIndex={tabIndex}
            maxLength={maxLength}
            spellCheck={false}
          />
          {invalid && (
            <div className="rounded-border pointer-events-none rounded-xl bg-gradient-to-r from-accent-muted/60 to-accent-muted opacity-60 dark:opacity-30" />
          )}
          {!!valid && (
            <Icon
              className={clsx("w-3.5 absolute bg-green", isPassword ? "right-12.5" : "right-5")}
              name="checked"
            />
          )}
          {(isPassword || children || loading) && (
            <div className="control-right">
              {children}
              {isPassword && (
                <button
                  type="button"
                  onClick={() => onChangePasswordVisible(!passwordVisible)}
                  className="opacity-60 hover:opacity-100 transition p-2"
                  tabIndex={-1}
                >
                  <Icon name={passwordVisible ? "eye-closed" : "eye"} className="w-4.5" />
                </button>
              )}
              {loading && <div className="mz-loader w-5 mr-1.5" />}
            </div>
          )}
        </div>
        {invalid && <span className="control-status control-status--error">*{invalid}</span>}
        {valid && isString(valid) && (
          <span className="control-status control-status--valid">*{valid}</span>
        )}
      </div>
    );
  },
);

TextInput.displayName = "TextInput";

export default TextInput;
